import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Modal, Badge, Pagination, Spinner, Alert } from 'react-bootstrap';
import { FaSync, FaDesktop } from 'react-icons/fa';
import { licenseAPI } from '../services/api';
import { toast } from 'react-toastify';

const Licenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [resetReason, setResetReason] = useState('');

  useEffect(() => { fetchLicenses(); }, [page, search, filter]);

  const fetchLicenses = async () => {
    try {
      const response = await licenseAPI.getAll({ page, search, status: filter !== 'all' ? filter : undefined });
      setLicenses(response.data.data);
      setTotalPages(response.data.pagination.pages);
    } catch (error) { toast.error('Failed to load licenses'); }
    finally { setLoading(false); }
  };

  const handleResetDevice = async (e) => {
    e.preventDefault();
    try {
      await licenseAPI.resetDevice({ license_key: selectedLicense.license_key, reason: resetReason });
      toast.success('Device reset successful');
      setShowResetModal(false);
      fetchLicenses();
      setResetReason('');
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to reset device'); }
  };

  const getStatusBadge = (status) => {
    const variants = { active: 'success', expired: 'danger', trial: 'warning', disabled: 'secondary' };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getPackageBadge = (pkg) => {
    const colors = { 'Trial': 'warning', '6 Months': 'info', '1 Year': 'primary', '3 Years': 'success' };
    return <Badge bg={colors[pkg] || 'secondary'}>{pkg}</Badge>;
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Licenses</h3>
        <div className="d-flex gap-2">
          <Form.Select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: '150px' }}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="expired">Expired</option>
            <option value="disabled">Disabled</option>
          </Form.Select>
        </div>
      </div>

      <Form.Control type="text" placeholder="Search by license key or user..." value={search} onChange={(e) => setSearch(e.target.value)} className="mb-3" />

      <Table striped bordered hover responsive>
        <thead>
          <tr><th>License Key</th><th>User</th><th>Package</th><th>Status</th><th>Valid Until</th><th>Device</th><th>Resets</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {licenses.map((license) => (
            <tr key={license.id}>
              <td><code>{license.license_key}</code></td>
              <td>{license.User?.name}</td>
              <td>{getPackageBadge(license.Package?.name)}</td>
              <td>{getStatusBadge(license.status)}</td>
              <td>{new Date(license.valid_until).toLocaleDateString()}</td>
              <td>{license.device_bound ? <Badge bg="success">Bound</Badge> : <Badge bg="secondary">Unbound</Badge>}</td>
              <td>{license.reset_used} / {license.reset_limit}</td>
              <td>
                {license.reset_used < license.reset_limit && (
                  <Button variant="warning" size="sm" onClick={() => { setSelectedLicense(license); setShowResetModal(true); }}><FaSync /> Reset</Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Pagination>{[...Array(totalPages)].map((_, i) => (
        <Pagination.Item key={i + 1} active={i + 1 === page} onClick={() => setPage(i + 1)}>{i + 1}</Pagination.Item>
      ))}</Pagination>

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton><Modal.Title>Reset Device</Modal.Title></Modal.Header>
        <Form onSubmit={handleResetDevice}>
          <Modal.Body>
            <Alert variant="warning">
              <strong>License:</strong> {selectedLicense?.license_key}<br/>
              <strong>User:</strong> {selectedLicense?.User?.name}<br/>
              <strong>Resets Used:</strong> {selectedLicense?.reset_used} / {selectedLicense?.reset_limit}
            </Alert>
            <Form.Group><Form.Label>Reason for Reset</Form.Label><Form.Control as="textarea" rows={3} required value={resetReason} onChange={(e) => setResetReason(e.target.value)} placeholder="Enter reason for device reset..." /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowResetModal(false)}>Cancel</Button><Button variant="warning" type="submit"><FaDesktop /> Reset Device</Button></Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Licenses;
